/**
 * Every page of the website, in one table.
 *
 * The router turns these into routes, the sitemap lists them, the prerender
 * writes one HTML file per entry and the title/description of a page comes
 * from here. One table rather than four so a new page cannot exist as a route
 * without also being findable - which is the whole point of this port: before
 * it, everything was loaded into one URL by AJAX and Google saw a single page.
 *
 * Deliberately free of any Vue import: vite.config.ts and the prerender script
 * read this file in node.
 */

export interface PageMeta {
  /** The route path, and the URL a visitor sees. German, like the site. */
  path: string;
  /** Route name, used for links and for the component lookup in the router. */
  name: string;
  /** The full <title>. Written out rather than assembled, so each reads well. */
  title: string;
  /** The <meta name="description">. German, roughly 120-160 characters. */
  description: string;
  /**
   * Listed in sitemap.xml and rendered ahead of time. False for the
   * catch-all, which has no URL of its own and nothing to index.
   */
  indexed: boolean;
  /** Relative weight in the sitemap. */
  priority: number;
}

export const SITE_NAME = "tubetänzig";

export const PAGES: PageMeta[] = [
  {
    path: "/",
    name: "start",
    title: "tubetänzig – Ländlerkapelle aus Laufenburg",
    description:
      "Traditionelle Schweizer Volksmusik von der Ländlerkapelle tubetänzig " +
      "aus Laufenburg: kommende Auftritte, Videos und Bilder.",
    indexed: true,
    priority: 1.0,
  },
  {
    path: "/auftritte",
    name: "auftritte",
    title: "Auftritte – tubetänzig",
    description:
      "Alle Auftritte der Ländlerkapelle tubetänzig: die kommenden Konzerte " +
      "und Feste sowie ein Rückblick auf die vergangenen Auftritte.",
    indexed: true,
    priority: 0.9,
  },
  {
    path: "/medien",
    name: "medien",
    title: "Medien – tubetänzig",
    description:
      "Videos und Bilder der Ländlerkapelle tubetänzig aus Laufenburg: " +
      "Aufnahmen von Auftritten und Impressionen aus der Stube.",
    indexed: true,
    priority: 0.8,
  },
  {
    path: "/ueber-uns",
    name: "ueberUns",
    title: "Über uns – tubetänzig",
    description:
      "Fünf Musikantinnen und Musikanten aus Laufenburg mit zwei Klarinetten, " +
      "zwei Schwyzerörgeli und Kontrabass – das ist tubetänzig.",
    indexed: true,
    priority: 0.7,
  },
  {
    path: "/kontakt",
    name: "kontakt",
    title: "Kontakt – tubetänzig",
    description:
      "Die Ländlerkapelle tubetänzig für Ihr Fest buchen oder eine Frage " +
      "stellen: Schreiben Sie uns über das Kontaktformular.",
    indexed: true,
    priority: 0.6,
  },
  {
    path: "/impressum",
    name: "impressum",
    title: "Impressum und Datenschutz – tubetänzig",
    description:
      "Impressum und Datenschutzerklärung der Webseite der Ländlerkapelle " +
      "tubetänzig aus Laufenburg.",
    indexed: true,
    priority: 0.3,
  },
  {
    // Vue Router's catch-all. Never linked, never listed, never prerendered.
    path: "/:pfad(.*)*",
    name: "nichtGefunden",
    title: "Seite nicht gefunden – tubetänzig",
    description:
      "Diese Seite gibt es nicht. Zurück zur Startseite der Ländlerkapelle " +
      "tubetänzig.",
    indexed: false,
    priority: 0.0,
  },
];

/** The pages that get a URL of their own: sitemap, prerender, navigation. */
export const INDEXED_PAGES = PAGES.filter((page) => page.indexed);

export function pageByName(name: string): PageMeta {
  const page = PAGES.find((entry) => entry.name === name);
  if (!page) {
    throw new Error(`Unbekannte Seite: ${name}`);
  }
  return page;
}
